import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import type { IllustrationLink } from '../data/procedurePhotos.ts'
import { readPhoto } from './lane.ts'

/** M16-I1: a linked picture as the device can show it (`readPhoto`: the device's own copy, the cache, then storage, cached from then on). A URL while it is held,
 *  null while it loads, false when it is neither on the device nor reachable now. The object URL is let go with the element. */
export function usePhotoUrl(link: Pick<IllustrationLink, 'sha256' | 'mime' | 'site_id' | 'org_id'> | null, local?: Blob): string | null | false {
  const { transport, org } = useClub()
  const [url, setUrl] = useState<string | null | false>(null)
  const sha = link?.sha256, mime = link?.mime, site = link?.site_id, linkOrg = link?.org_id
  useEffect(() => {
    setUrl(null)
    if (!sha || !mime || !site) return
    let live = true; let revoke: string | null = null
    void readPhoto({ transport }, { sha256: sha, mime, site_id: site, ...(linkOrg ? { org_id: linkOrg } : {}) } as IllustrationLink, org.org_id, local)
      .then((b) => { if (!live) return; if (b) { revoke = URL.createObjectURL(b); setUrl(revoke) } else setUrl(false) })
    return () => { live = false; if (revoke) URL.revokeObjectURL(revoke) }
  }, [sha, mime, site, linkOrg, local, transport, org.org_id])
  return url
}

/** A URL for bytes this device holds (a picture just taken), let go with the element. */
export function useBlobUrl(blob: Blob | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) { setUrl(null); return }
    const u = URL.createObjectURL(blob); setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [blob])
  return url
}

/** The picture, or what stands in for it: "loading" while the device looks, "not on this device" when nothing answers (no signal and never opened here). */
export function IllustrationImage({ link, local, alt, className, boxClassName, testId = 'illustration' }: { link: IllustrationLink; local?: Blob; alt: string; className: string; boxClassName?: string; testId?: string }) {
  const { t } = useTranslation()
  const url = usePhotoUrl(link, local)
  if (url) return <img src={url} alt={alt} className={className} data-testid={`${testId}-image`} data-sha={link.sha256} />
  return (
    <span className={`${boxClassName ?? className} flex items-center justify-center bg-slate-100 p-2 text-center text-[13px] text-slate-600 dark:bg-slate-800 dark:text-slate-300`} data-testid={`${testId}-placeholder`} data-state={url === false ? 'unavailable' : 'loading'}>
      {url === false ? t('plan.illustrationOffline') : t('common.loading')}
    </span>
  )
}
